import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Editor from '@monaco-editor/react';
import Form from '../components/Form/Form';
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import UiInputText from '../components/Form/UiInputText';
import UiInputList from '../components/Form/UiInputList';
import UiInputCheckbox from '../components/Form/UiInputCheckbox';
import { UiVerticalTabs, VerticalTab } from '../components/Form/UiVerticalTabs';
import { useNotification } from '../components/Core/NotificationProvider';
import { post } from '../services/ApiService';

const tipoList = [
    { id: 'banco', title: 'Banco' },
    { id: 'intermediador', title: 'Intermediador de Pagamento' },
];

interface ShowIf {
    field: string;
    equals: string;
}

interface SchemaField {
    name: string;
    label: string;
    type: 'text' | 'password' | 'select';
    options?: Record<string, string>;
    showIf?: ShowIf;
}

interface SchemaAction {
    label: string;
    icon?: string;
    route: string;
    action: string;
    method?: string;
    confirm?: string;
    showIf?: ShowIf;
    successMessage?: string;
}

interface GatewaySchema {
    fields: SchemaField[];
    actions: SchemaAction[];
}

export default function GatewayPagamentoForm(props: any) {
    const methods = useForm({ defaultValues: props.data || {} });
    const { handleSubmit, reset, watch, setValue, getValues } = methods;
    const notification = useNotification();

    const [schemaText, setSchemaText] = useState('');
    const [schemaError, setSchemaError] = useState<string | null>(null);
    const [parsedSchema, setParsedSchema] = useState<GatewaySchema | null>(null);

    useEffect(() => {
        if (props.data) {
            if (props.data.schema) {
                const text = JSON.stringify(props.data.schema, null, 2);
                setSchemaText(text);
                setParsedSchema(props.data.schema);
            }
            reset(props.data);
        }
    }, [props.data, reset]);

    const handleSchemaChange = (text: string) => {
        setSchemaText(text);
        try {
            const parsed = JSON.parse(text);
            setParsedSchema(parsed);
            setValue('schema', parsed);
            setSchemaError(null);
        } catch (e: any) {
            setSchemaError('JSON inválido: ' + e.message);
        }
    };

    const isVisible = (showIf?: ShowIf) => {
        if (!showIf) return true;
        return watch(`configuracao.${showIf.field}`) === showIf.equals;
    };

    const handleAction = async (action: SchemaAction) => {
        if (action.confirm && !window.confirm(action.confirm)) return;

        const vals = getValues();
        if (!vals.id) {
            notification.error('Salve o gateway antes de executar ações');
            return;
        }

        let extra: Record<string, any> = {};
        if (action.action === 'simularPagamento') {
            const codigo = window.prompt('Informe o codigoSolicitacao do boleto:');
            if (!codigo) return;
            extra = { codigoSolicitacao: codigo };
        }

        try {
            const response = await post(`/?route=${action.route}&action=${action.action}`, {
                dados: { id: vals.id, configuracao: vals.configuracao, ...extra }
            });
            if (response.data?.success) {
                notification.success(response.data.message || action.successMessage || 'Sucesso!');
            } else {
                notification.error(response.data?.message || 'Erro ao executar ação');
            }
        } catch {
            notification.error('Erro de comunicação com o servidor');
        }
    };

    return (
        <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(props.triggerSubmit)} methods={methods}>
                <WindowBody noPadding>
                    <UiVerticalTabs>
                        <VerticalTab title="Configuração">
                            <fieldset>
                                    <legend>Identificação</legend>
                                    <div className="row">
                                        <UiInputText size={6} name="nome" label="Nome" />
                                        <UiInputList size={4} name="tipo" list={tipoList} idField="id" titleField="title" label="Tipo" />
                                        <UiInputCheckbox size={2} name="ativo" label="Ativo" />
                                    </div>
                                </fieldset>

                                {parsedSchema && (
                                    <fieldset>
                                        <legend>Configurações</legend>
                                        <div className="row">
                                            {parsedSchema.fields?.filter(f => isVisible(f.showIf)).map(field => {
                                                const name = `configuracao.${field.name}`;
                                                if (field.type === 'select') {
                                                    return (
                                                        <UiInputList
                                                            key={field.name}
                                                            size={6}
                                                            name={name}
                                                            label={field.label}
                                                            list={Object.entries(field.options || {}).map(([id, title]) => ({ id, title }))}
                                                            idField="id"
                                                            titleField="title"
                                                        />
                                                    );
                                                }
                                                return (
                                                    <UiInputText
                                                        key={field.name}
                                                        size={6}
                                                        name={name}
                                                        label={field.label}
                                                        type={field.type === 'password' ? 'password' : 'text'}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </fieldset>
                                )}

                                {parsedSchema?.actions && parsedSchema.actions.filter(a => isVisible(a.showIf)).length > 0 && (
                                    <fieldset>
                                        <legend>Ações</legend>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {parsedSchema.actions.filter(a => isVisible(a.showIf)).map(action => (
                                                <UiButton
                                                    key={action.action}
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={() => handleAction(action)}
                                                >
                                                    {action.label}
                                                </UiButton>
                                            ))}
                                        </div>
                                    </fieldset>
                                )}
                        </VerticalTab>

                        <VerticalTab title="Schema" noPadding>
                            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                                <Editor
                                    height="100%"
                                    defaultLanguage="json"
                                    value={schemaText}
                                    onChange={value => handleSchemaChange(value || '')}
                                    theme="vs-dark"
                                    options={{
                                        minimap: { enabled: false },
                                        fontSize: 13,
                                        fontFamily: 'Consolas, "Courier New", monospace',
                                        scrollBeyondLastLine: false,
                                        formatOnPaste: true,
                                        tabSize: 2,
                                    }}
                                />
                            </div>
                            <div className="px-3 py-1" style={{ borderTop: '1px solid #dee2e6', flexShrink: 0 }}>
                                {schemaError
                                    ? <span className="text-danger small">{schemaError}</span>
                                    : parsedSchema && <span className="text-success small">✓ JSON válido</span>
                                }
                            </div>
                        </VerticalTab>
                    </UiVerticalTabs>
                </WindowBody>
                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                    <UiButton variant="primary" type="submit">Salvar</UiButton>
                </WindowFooter>
            </Form>
        </FormProvider>
    );
}
